import type { FC } from 'react';

import type { SanityPolicyEngineAnimation } from '#/lib/sanity/queries/policyEngineAnimation';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';
import { ButtonRow } from '#/components/ButtonRow';
import { formatHeadline } from '#/components/Headline';

import { PolicyEngineCanvas } from './PolicyEngineCanvas';

export const PolicyEngineAnimation: FC<
  SanityPolicyEngineAnimation & PageLayoutSharedProps
> = (props) => {
  const metrics = props.metrics?.filter((m) => m.number && m.label) ?? [];
  const isFirst = props.sectionIndex === 0;
  const buttonsBelow = props.buttonPosition === 'below';

  const buttons = props.buttons?.length ? (
    <div
      className="@container flex w-full justify-center"
      data-sanity={createSanityFieldDataAttribute(
        props.sanitySource,
        'buttons',
      )}
    >
      <ButtonRow buttons={props.buttons} theme="dark" />
    </div>
  ) : null;

  return (
    <section
      className={cn(
        'overflow-hidden bg-charcoal text-cloud',
        // First section: extend under the fixed header (same as other heroes)
        isFirst &&
          'mt-[calc(var(--header-height)*-1)] pt-[var(--header-height)]',
      )}
    >
      <div className="flex flex-col items-center gap-8 py-64 text-center m:gap-16 m:py-80">
        <div className="p-main mx-auto flex w-full max-w-[924px] flex-col items-center gap-32 m:gap-56">
          {/* Figma: 40px regular title, 24px supporting line, both white */}
          <div className="flex w-full flex-col items-center gap-24 m:gap-38">
            <h2
              className="type-heading4 m:type-heading3 text-balance"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'title',
              )}
            >
              {formatHeadline(props.title, 'text-cipher')}
            </h2>

            {props.headline ? (
              <p
                className="type-heading6 m:type-heading5 max-w-[800px] text-balance"
                data-sanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'headline',
                )}
              >
                {formatHeadline(props.headline, 'text-cipher')}
              </p>
            ) : null}
          </div>

          {!buttonsBelow ? buttons : null}
        </div>

        {metrics.length ? (
          // Match site content width (same as heroes / header: max-w-1500)
          <div
            className="mx-auto w-full max-w-1500"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'metrics',
            )}
          >
            <PolicyEngineCanvas metrics={metrics} />
          </div>
        ) : null}

        {buttonsBelow ? (
          <div className="p-main mx-auto flex w-full max-w-[924px] justify-center">
            {buttons}
          </div>
        ) : null}
      </div>
    </section>
  );
};
